import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getEmailBranding, updateEmailBranding } from '../../../services/SettingsService';

const MAX_ICON_BYTES = 500 * 1024; // icons are tiny — 500KB is already generous

function isSafeLinkUrl(url) {
    const val = (url || '').trim().toLowerCase();
    return val.startsWith('http://') || val.startsWith('https://') || val.startsWith('mailto:') || val.startsWith('tel:');
}

// Same upload pattern already used for the business profile logo (Invoices.js):
// <input type="file"> -> FileReader.readAsDataURL -> base64 data URL stored as-is via the
// JSON API. No separate upload endpoint, no filesystem storage.
function AssetUploader({ label, value, onChange, inputRef }) {
    const { t } = useTranslation();

    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error(t('emailTemplates.branding.invalidType'));
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => onChange(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemove = () => {
        onChange('');
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div>
            <label className="form-label fw-semibold">{label}</label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
                <div
                    className="border rounded d-flex align-items-center justify-content-center bg-light"
                    style={{ width: 140, height: 80 }}
                >
                    {value ? (
                        <img src={value} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    ) : (
                        <small className="text-muted">{t('emailTemplates.branding.notSet')}</small>
                    )}
                </div>
                <div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        className="form-control form-control-sm mb-1"
                        style={{ maxWidth: 240 }}
                        onChange={handleUpload}
                    />
                    {value && (
                        <Button variant="link" size="sm" className="text-danger p-0" onClick={handleRemove}>
                            {t('emailTemplates.branding.remove')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function SocialIconsEditor({ icons, onChange }) {
    const { t } = useTranslation();
    const [newLabel, setNewLabel] = useState('');
    const [newUrl, setNewUrl] = useState('');
    const [newImage, setNewImage] = useState('');
    const fileInputRef = useRef(null);

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error(t('emailTemplates.branding.invalidType'));
            return;
        }
        if (file.size > MAX_ICON_BYTES) {
            toast.error(t('emailTemplates.branding.errors.imageTooLarge'));
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => setNewImage(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleAdd = () => {
        if (!newLabel.trim()) {
            toast.error(t('emailTemplates.branding.errors.missingIconLabel'));
            return;
        }
        if (!newImage) {
            toast.error(t('emailTemplates.branding.errors.missingIconImage'));
            return;
        }
        if (!isSafeLinkUrl(newUrl)) {
            toast.error(t('emailTemplates.branding.errors.invalidIconUrl'));
            return;
        }
        onChange([...icons, {
            id: `icon-${Date.now()}`,
            label: newLabel.trim(),
            url: newUrl.trim(),
            image_base64: newImage,
        }]);
        setNewLabel('');
        setNewUrl('');
        setNewImage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemove = (id) => {
        onChange(icons.filter((icon) => icon.id !== id));
    };

    // Reorders without touching any icon's image, label or URL: the list order is the order in the emails.
    const handleMove = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= icons.length) return;
        const next = [...icons];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <div>
            <label className="form-label fw-semibold d-block">{t('emailTemplates.branding.socialIconsLabel')}</label>
            <div className="text-muted small mb-2">{t('emailTemplates.branding.socialIconsHint')}</div>

            {icons.length > 0 && (
                <div className="d-flex flex-column gap-2 mb-3">
                    {icons.map((icon, index) => (
                        <div key={icon.id} className="d-flex align-items-center gap-2 border rounded p-2">
                            <img src={icon.image_base64} alt="" style={{ width: 24, height: 24 }} />
                            <div className="flex-grow-1">
                                <div className="fw-semibold small">{icon.label}</div>
                                <div className="text-muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>{icon.url}</div>
                            </div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="px-2 py-0"
                                disabled={index === 0}
                                aria-label={t('emailTemplates.branding.moveIconUp', { label: icon.label })}
                                title={t('emailTemplates.branding.moveIconUp', { label: icon.label })}
                                onClick={() => handleMove(index, -1)}
                            >
                                ↑
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="px-2 py-0"
                                disabled={index === icons.length - 1}
                                aria-label={t('emailTemplates.branding.moveIconDown', { label: icon.label })}
                                title={t('emailTemplates.branding.moveIconDown', { label: icon.label })}
                                onClick={() => handleMove(index, 1)}
                            >
                                ↓
                            </Button>
                            <Button variant="link" size="sm" className="text-danger p-0" onClick={() => handleRemove(icon.id)}>
                                {t('emailTemplates.branding.removeIcon')}
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div className="border rounded p-2 bg-light">
                <div className="text-muted small mb-2">{t('emailTemplates.branding.addIconLabel')}</div>
                <Row className="g-2 align-items-end">
                    <Col xs={12} sm={3}>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            className="form-control form-control-sm"
                            onChange={handleFile}
                        />
                    </Col>
                    <Col xs={6} sm={3}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('emailTemplates.branding.iconLabelPlaceholder')}
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                        />
                    </Col>
                    <Col xs={6} sm={4}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('emailTemplates.branding.iconUrlPlaceholder')}
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} sm={2}>
                        <Button size="sm" variant="outline-primary" className="w-100" onClick={handleAdd}>
                            {t('emailTemplates.branding.addIcon')}
                        </Button>
                    </Col>
                </Row>
                {newImage && (
                    <div className="mt-2">
                        <img src={newImage} alt="" style={{ width: 24, height: 24 }} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function EmailBrandingSection({ onSaved }) {
    const { t } = useTranslation();
    const [logo, setLogo] = useState('');
    const [signatureIcons, setSignatureIcons] = useState([]);
    const [pdfLanguage, setPdfLanguage] = useState('nl');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await getEmailBranding();
            setLogo(res.data?.logo_base64 || '');
            setSignatureIcons(Array.isArray(res.data?.signature_icons) ? res.data.signature_icons : []);
            setPdfLanguage(res.data?.invoice_pdf_language || 'nl');
        } catch {
            toast.error(t('emailTemplates.branding.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await updateEmailBranding({
                logo_base64: logo,
                signature_icons: signatureIcons,
                invoice_pdf_language: pdfLanguage,
            });
            toast.success(t('emailTemplates.branding.success.saved'));
            if (onSaved) onSaved(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.error || t('emailTemplates.branding.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="mb-4">
            <Card.Header>
                <strong>{t('emailTemplates.branding.title')}</strong>
                <div className="text-muted small">{t('emailTemplates.branding.subtitle')}</div>
            </Card.Header>
            <Card.Body>
                {loading ? (
                    <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                ) : (
                    <>
                        <Row className="g-4">
                            <Col md={6}>
                                <AssetUploader
                                    label={t('emailTemplates.branding.logoLabel')}
                                    value={logo}
                                    onChange={setLogo}
                                    inputRef={logoInputRef}
                                />
                                <div className="text-muted small mt-2">{t('emailTemplates.branding.logoReuseHint')}</div>
                            </Col>
                            <Col md={6}>
                                <label className="form-label fw-semibold d-block">
                                    {t('emailTemplates.branding.pdfLanguageLabel')}
                                </label>
                                <div className="text-muted small mb-2">
                                    {t('emailTemplates.branding.pdfLanguageHint')}
                                </div>
                                <div className="d-flex gap-1">
                                    <Button
                                        size="sm"
                                        variant={pdfLanguage === 'nl' ? 'primary' : 'outline-secondary'}
                                        onClick={() => setPdfLanguage('nl')}
                                        style={{ minWidth: 46, fontSize: 12 }}
                                    >
                                        🇳🇱 NL
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={pdfLanguage === 'en' ? 'primary' : 'outline-secondary'}
                                        onClick={() => setPdfLanguage('en')}
                                        style={{ minWidth: 46, fontSize: 12 }}
                                    >
                                        🇬🇧 EN
                                    </Button>
                                </div>
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        <SocialIconsEditor icons={signatureIcons} onChange={setSignatureIcons} />

                        <Button variant="primary" size="sm" className="mt-4" onClick={handleSave} disabled={saving}>
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
